// Search / filter field (DESIGN_SPEC §10.7): icon left, <kbd>/</kbd> right.
//   <Search value onChange label placeholder? hotkey? width? className? inputRef? />
//   hotkey (default true): "/" focuses it from anywhere that isn't a text field (skipped if another handler already
//   preventDefault-ed the key); pass hotkey={false} when the page binds "/" through useHotkeys and focus via inputRef.
//   Esc clears (when non-empty) and blurs.
import { useEffect, useRef, type Ref } from "react";
import { cx } from "./cx";

function typingIn(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT";
}

export function Search({
  value,
  onChange,
  label,
  placeholder,
  hotkey = true,
  width,
  className,
  inputRef,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  hotkey?: boolean;
  width?: string;
  className?: string;
  inputRef?: Ref<HTMLInputElement>;
}) {
  const own = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!hotkey) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented || typingIn(document.activeElement)) return;
      e.preventDefault();
      own.current?.focus();
      own.current?.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hotkey]);

  const setRefs = (el: HTMLInputElement | null) => {
    own.current = el;
    if (typeof inputRef === "function") inputRef(el);
    else if (inputRef) inputRef.current = el;
  };

  return (
    <label className={cx("search", className)} style={width ? { width } : undefined}>
      <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.25">
        <circle cx="7" cy="7" r="4.5" />
        <path d="M10.4 10.4l3.85 3.85" />
      </svg>
      <input
        ref={setRefs}
        className="input"
        type="text"
        role="searchbox"
        enterKeyHint="search"
        value={value}
        placeholder={placeholder}
        aria-label={label}
        spellCheck={false}
        autoComplete="off"
        aria-keyshortcuts={hotkey ? "/" : undefined}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Escape") return;
          e.preventDefault();
          if (value) onChange("");
          e.currentTarget.blur();
        }}
      />
      {hotkey && <kbd aria-hidden="true">/</kbd>}
    </label>
  );
}
